import React, { useEffect, useRef, useState } from 'react';
import './CustomCursor.css';
import {
  shouldEnableCustomCursor,
  CUSTOM_CURSOR_ACTIVE_CLASS,
  TARGETING_CURSOR_SUPPRESS_CLASS,
  NEGARA_OUTRO_ACTIVE_CLASS,
  CURSOR_Z_INDEX,
} from '../config/cursorConfig';

/**
 * CustomCursor Component
 *
 * Renders a custom cursor that is visible in Discord/OBS streams.
 * The Windows cursor is not captured by screen recording software,
 * but this React-rendered cursor is.
 *
 * Performance:
 * - Position is written to the DOM via ref + rAF (no React re-render per move).
 * - Pointer styling skips getComputedStyle on the War Effort SVG map.
 * - OS cursor is hidden via CSS (`html.custom-cursor-active *`); no per-node inline walk.
 */

function computeIsPointer(target) {
  if (!target || target.nodeType !== 1) return false;
  if (
    target.closest?.(
      '.eu-negara-outro-continue, .eu-negara-outro-white-veil[role="button"]'
    )
  ) {
    return true;
  }
  // Skip getComputedStyle on painted SVG paths (forces layout). Keep one cursor
  // size over the map so country-border target changes cannot hitch the pointer.
  if (target.closest?.('.world-map-container')) {
    return false;
  }
  if (
    target.tagName === 'BUTTON' ||
    target.tagName === 'A' ||
    target.onclick != null ||
    target.classList.contains('clickable') ||
    target.closest?.('button, a, [role="button"]')
  ) {
    return true;
  }
  const cursorStyle = window.getComputedStyle(target).cursor;
  return cursorStyle === 'pointer' || cursorStyle === 'help';
}

const CustomCursor = () => {
  const enabled = shouldEnableCustomCursor();
  const elRef = useRef(null);
  const posRef = useRef({ x: -100, y: -100 });
  const lastTargetRef = useRef(null);
  const rafIdRef = useRef(null);

  const [battleTargetingReticleActive, setBattleTargetingReticleActive] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains(TARGETING_CURSOR_SUPPRESS_CLASS);
  });

  const [negaraOutroActive, setNegaraOutroActive] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains(NEGARA_OUTRO_ACTIVE_CLASS);
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const sync = () => {
      setBattleTargetingReticleActive(root.classList.contains(TARGETING_CURSOR_SUPPRESS_CLASS));
      setNegaraOutroActive(root.classList.contains(NEGARA_OUTRO_ACTIVE_CLASS));
    };
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    sync();
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled || battleTargetingReticleActive) {
      document.documentElement.classList.remove(CUSTOM_CURSOR_ACTIVE_CLASS);
      return undefined;
    }

    // Use bare `none` (not a custom url). Chromium scrollbars inherit the scroll
    // container's cursor but often fail custom cursor URLs and fall back to the OS arrow.
    const hiddenOsCursor = 'none';

    document.documentElement.classList.add(CUSTOM_CURSOR_ACTIVE_CLASS);
    document.body.style.setProperty('cursor', hiddenOsCursor, 'important');
    document.documentElement.style.setProperty('cursor', hiddenOsCursor, 'important');

    const flushPosition = () => {
      rafIdRef.current = null;
      const el = elRef.current;
      if (!el) return;
      const { x, y } = posRef.current;
      el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    };

    const scheduleFlush = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(flushPosition);
    };

    const handleMouseMove = (e) => {
      posRef.current = { x: e.clientX, y: e.clientY };

      if (lastTargetRef.current !== e.target) {
        lastTargetRef.current = e.target;
        const isPointer = computeIsPointer(e.target);
        const el = elRef.current;
        if (el) {
          el.classList.toggle('custom-cursor-pointer', isPointer);
        }
      }

      scheduleFlush();
    };

    const handleMouseLeave = () => {
      posRef.current = { x: -100, y: -100 };
      lastTargetRef.current = null;
      const el = elRef.current;
      if (el) {
        el.classList.remove('custom-cursor-pointer');
      }
      scheduleFlush();
    };

    const handleMouseEnter = (e) => {
      posRef.current = { x: e.clientX, y: e.clientY };
      const surface = elRef.current;
      if (surface && typeof document.elementFromPoint === 'function') {
        const t = document.elementFromPoint(e.clientX, e.clientY);
        lastTargetRef.current = t || null;
        if (t) {
          surface.classList.toggle('custom-cursor-pointer', computeIsPointer(t));
        } else {
          surface.classList.remove('custom-cursor-pointer');
        }
      } else if (surface) {
        lastTargetRef.current = null;
        surface.classList.remove('custom-cursor-pointer');
      }
      scheduleFlush();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mouseenter', handleMouseEnter);
      document.documentElement.classList.remove(CUSTOM_CURSOR_ACTIVE_CLASS);
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [enabled, battleTargetingReticleActive]);

  if (!enabled || battleTargetingReticleActive) {
    return null;
  }

  const cursorZIndex = negaraOutroActive
    ? CURSOR_Z_INDEX.customCursorAboveNegaraOutro
    : CURSOR_Z_INDEX.customCursor;

  return (
    <div
      ref={elRef}
      className="custom-cursor"
      style={{ zIndex: cursorZIndex }}
    />
  );
};

export default CustomCursor;
